import {
  DEFAULT_OPERATIONAL_CONTROL_SETTINGS,
  type OperationalControlSettings
} from './operational-control-settings';
import type { OperationalStatus } from './types';

export type ExecutiveMetricDomain = 'CONTEXT' | 'PERFORMANCE' | 'RISK' | 'EXCEPTION';

export type ExecutiveSemanticState =
  | 'OPTIMAL'
  | 'STABLE'
  | 'ATTENTION'
  | 'CRITICAL'
  | 'NEUTRAL'
  | 'STARTUP'
  | 'STALE'
  | 'NOT_CONFIGURED'
  | 'NO_OBLIGATION'
  | 'NOT_APPLICABLE'
  | 'NO_CAPTURE'
  | 'NO_PLAN'
  | 'NOT_STARTED'
  | 'TECHNICAL_ERROR';

export type ExecutiveSemanticPresentation = {
  state: ExecutiveSemanticState;
  label: string;
  description: string;
  indicator: string;
  badgeClass: string;
  textClass: string;
  cardClass: string;
};

export const EXECUTIVE_LABELS = {
  health: 'Salud',
  vicepresidencies: 'Vicepresidencias',
  instances: 'Instancias',
  plan: 'Plan',
  indicators: 'Indicadores',
  performance: 'Desempeño',
  capture: 'Captura',
  alerts: 'Alertas',
  exceptions: 'Excepciones',
  critical: 'Críticas',
  attention: 'Atención',
  stale: 'Rezago',
  startup: 'Arranque',
  notConfigured: 'Sin configurar'
} as const;

export const EXECUTIVE_TOOLTIPS = {
  vicepresidencies: '13 vicepresidencias y Secretaría General.',
  health: 'Resultado nacional del modelo de disciplina operativa.',
  plan: 'Avance acumulado de los planes activos.',
  indicators: 'Cumplimiento de los indicadores con obligación aplicable.',
  capture: 'Instancias que registraron la información requerida del periodo.',
  startup: 'Fase inicial de operación; se monitorea sin penalización.',
  stale: 'Actualización fuera del plazo establecido.',
  notConfigured: 'Faltan parámetros para realizar la evaluación.',
  noObligation: 'No existe obligación aplicable en el periodo.',
  notApplicable: 'La métrica no corresponde al caso.',
  noCapture: 'No se registró información del periodo.',
  noPlan: 'No existe un plan activo para el periodo.',
  error: 'No fue posible recuperar o evaluar la información.',
  performance: 'Resultado ponderado de las dimensiones aplicables.',
  instances: 'Instancias visibles dentro del alcance autorizado.',
  alerts: 'Incidencias críticas y de atención que requieren seguimiento.',
  exceptions: 'Casos excluidos o protegidos del cálculo de desempeño.'
} as const;

export const EXECUTIVE_METRIC_DOMAINS = {
  Salud: 'PERFORMANCE',
  Vicepresidencias: 'CONTEXT',
  Instancias: 'CONTEXT',
  Plan: 'PERFORMANCE',
  Indicadores: 'PERFORMANCE',
  Desempeño: 'PERFORMANCE',
  Captura: 'PERFORMANCE',
  Alertas: 'RISK',
  Excepciones: 'EXCEPTION'
} as const satisfies Record<string, ExecutiveMetricDomain>;

export const EXECUTIVE_STATE_PRESENTATION: Record<ExecutiveSemanticState, ExecutiveSemanticPresentation> = {
  OPTIMAL: {
    state: 'OPTIMAL', label: 'Óptimo', description: 'Resultado dentro del nivel óptimo.', indicator: '●',
    badgeClass: 'border-emerald-400/50 bg-emerald-950 text-emerald-100',
    textClass: 'text-emerald-300', cardClass: 'border-emerald-500/35 bg-emerald-950/45'
  },
  STABLE: {
    state: 'STABLE', label: 'Estable', description: 'Resultado estable con seguimiento ordinario.', indicator: '◆',
    badgeClass: 'border-yellow-400/50 bg-yellow-950 text-yellow-100',
    textClass: 'text-yellow-300', cardClass: 'border-yellow-500/35 bg-yellow-950/45'
  },
  ATTENTION: {
    state: 'ATTENTION', label: 'Atención', description: 'Resultado que requiere atención ejecutiva.', indicator: '▲',
    badgeClass: 'border-orange-400/50 bg-orange-950 text-orange-100',
    textClass: 'text-orange-300', cardClass: 'border-orange-500/35 bg-orange-950/45'
  },
  CRITICAL: {
    state: 'CRITICAL', label: 'Crítico', description: 'Resultado crítico que requiere intervención.', indicator: '!',
    badgeClass: 'border-red-400/60 bg-red-950 text-red-100',
    textClass: 'text-red-300', cardClass: 'border-red-500/45 bg-red-950/50'
  },
  NEUTRAL: {
    state: 'NEUTRAL', label: 'Contexto', description: 'Dato de contexto; no representa desempeño.', indicator: '■',
    badgeClass: 'border-slate-500/60 bg-slate-900 text-slate-200',
    textClass: 'text-slate-200', cardClass: 'border-slate-700 bg-slate-950/80'
  },
  STARTUP: {
    state: 'STARTUP', label: 'Arranque', description: EXECUTIVE_TOOLTIPS.startup, indicator: '◆',
    badgeClass: 'border-sky-400/50 bg-sky-950 text-sky-100',
    textClass: 'text-sky-300', cardClass: 'border-sky-500/35 bg-sky-950/45'
  },
  STALE: {
    state: 'STALE', label: 'Rezago', description: EXECUTIVE_TOOLTIPS.stale, indicator: '▲',
    badgeClass: 'border-amber-400/50 bg-amber-950 text-amber-100',
    textClass: 'text-amber-300', cardClass: 'border-amber-500/35 bg-amber-950/45'
  },
  NOT_CONFIGURED: {
    state: 'NOT_CONFIGURED', label: 'Sin configurar', description: EXECUTIVE_TOOLTIPS.notConfigured, indicator: '◇',
    badgeClass: 'border-amber-400/50 bg-amber-950 text-amber-100',
    textClass: 'text-amber-300', cardClass: 'border-amber-500/35 bg-amber-950/45'
  },
  NO_OBLIGATION: {
    state: 'NO_OBLIGATION', label: 'Sin obligación', description: EXECUTIVE_TOOLTIPS.noObligation, indicator: '○',
    badgeClass: 'border-zinc-600 bg-zinc-900 text-zinc-300',
    textClass: 'text-zinc-300', cardClass: 'border-zinc-700 bg-zinc-950/70'
  },
  NOT_APPLICABLE: {
    state: 'NOT_APPLICABLE', label: 'No aplica', description: EXECUTIVE_TOOLTIPS.notApplicable, indicator: '○',
    badgeClass: 'border-zinc-600 bg-zinc-900 text-zinc-300',
    textClass: 'text-zinc-300', cardClass: 'border-zinc-700 bg-zinc-950/70'
  },
  NO_CAPTURE: {
    state: 'NO_CAPTURE', label: 'Sin captura', description: EXECUTIVE_TOOLTIPS.noCapture, indicator: '!',
    badgeClass: 'border-red-400/60 bg-red-950 text-red-100',
    textClass: 'text-red-300', cardClass: 'border-red-500/45 bg-red-950/50'
  },
  NO_PLAN: {
    state: 'NO_PLAN', label: 'Sin plan', description: EXECUTIVE_TOOLTIPS.noPlan, indicator: '○',
    badgeClass: 'border-zinc-600 bg-zinc-900 text-zinc-300',
    textClass: 'text-zinc-300', cardClass: 'border-zinc-700 bg-zinc-950/70'
  },
  NOT_STARTED: {
    state: 'NOT_STARTED', label: 'Por iniciar', description: 'La obligación todavía no inicia en el periodo.', indicator: '◇',
    badgeClass: 'border-sky-400/50 bg-sky-950 text-sky-100',
    textClass: 'text-sky-300', cardClass: 'border-sky-500/35 bg-sky-950/45'
  },
  TECHNICAL_ERROR: {
    state: 'TECHNICAL_ERROR', label: 'Error', description: EXECUTIVE_TOOLTIPS.error, indicator: '!',
    badgeClass: 'border-red-400/60 bg-red-950 text-red-100',
    textClass: 'text-red-300', cardClass: 'border-red-500/45 bg-red-950/50'
  }
};

export const EXECUTIVE_LEGEND_STATES: ExecutiveSemanticState[] = [
  'OPTIMAL',
  'STABLE',
  'ATTENTION',
  'CRITICAL',
  'STARTUP',
  'STALE',
  'NOT_CONFIGURED',
  'NO_OBLIGATION',
  'NOT_APPLICABLE'
];

export const resolveOperationalSettings = (
  settings?: OperationalControlSettings
): OperationalControlSettings => settings ?? DEFAULT_OPERATIONAL_CONTROL_SETTINGS;

export const classifyPerformance = (
  value: number | null,
  settings?: OperationalControlSettings
): ExecutiveSemanticState => {
  if (value === null || !Number.isFinite(value)) return 'NOT_APPLICABLE';
  const active = resolveOperationalSettings(settings);
  if (value >= active.greenThreshold) return 'OPTIMAL';
  if (value >= active.yellowThreshold) return 'STABLE';
  if (value >= active.orangeThreshold) return 'ATTENTION';
  return 'CRITICAL';
};

export const classifyRiskCount = (
  count: number,
  kind: 'critical' | 'attention'
): ExecutiveSemanticState => count <= 0 ? 'OPTIMAL' : kind === 'critical' ? 'CRITICAL' : 'ATTENTION';

export const classifyOperationalStatus = (status: OperationalStatus): ExecutiveSemanticState => ({
  AL_DIA: 'OPTIMAL',
  ATENCION: 'ATTENTION',
  CRITICO: 'CRITICAL',
  ONBOARDING: 'STARTUP',
  NOT_STARTED: 'NOT_STARTED',
  SIN_DATOS: 'NO_CAPTURE',
  SIN_OBLIGACION: 'NO_OBLIGATION',
  SIN_CONFIGURACION: 'NOT_CONFIGURED',
  SIN_META_VENCIDA: 'NOT_STARTED'
}[status] as ExecutiveSemanticState);

export const classifyMetricException = (
  value: number | null,
  status: OperationalStatus | undefined,
  metric: 'plan' | 'indicators' | 'performance' | 'capture',
  settings?: OperationalControlSettings
): ExecutiveSemanticState => {
  if (value !== null) return classifyPerformance(value, settings);
  if (status === 'SIN_CONFIGURACION') return 'NOT_CONFIGURED';
  if (status === 'SIN_OBLIGACION') return 'NO_OBLIGATION';
  if (status === 'SIN_META_VENCIDA' || status === 'NOT_STARTED') return 'NOT_STARTED';
  if (status === 'ONBOARDING') return 'STARTUP';
  if (metric === 'plan') return 'NO_PLAN';
  if (metric === 'capture' && status === 'SIN_DATOS') return 'NO_CAPTURE';
  return 'NOT_APPLICABLE';
};

export const describeStale = (
  months: number,
  settings?: OperationalControlSettings
): ExecutiveSemanticPresentation => {
  if (months <= 0) return EXECUTIVE_STATE_PRESENTATION.OPTIMAL;
  const active = resolveOperationalSettings(settings);
  if (months >= active.staleCriticalMonths) {
    return {
      ...EXECUTIVE_STATE_PRESENTATION.STALE,
      badgeClass: EXECUTIVE_STATE_PRESENTATION.CRITICAL.badgeClass,
      textClass: EXECUTIVE_STATE_PRESENTATION.CRITICAL.textClass,
      cardClass: EXECUTIVE_STATE_PRESENTATION.CRITICAL.cardClass
    };
  }
  if (months >= active.staleWarningMonths) {
    return {
      ...EXECUTIVE_STATE_PRESENTATION.STALE,
      badgeClass: EXECUTIVE_STATE_PRESENTATION.ATTENTION.badgeClass,
      textClass: EXECUTIVE_STATE_PRESENTATION.ATTENTION.textClass,
      cardClass: EXECUTIVE_STATE_PRESENTATION.ATTENTION.cardClass
    };
  }
  return EXECUTIVE_STATE_PRESENTATION.STALE;
};
